import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArchiveBoxXMark,
  heroBanknotes,
  heroExclamationTriangle,
  heroLightBulb,
  heroPhoto,
  heroSparkles,
} from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import { businessCurrencyEnum } from '../../../core/api/generated/models/BusinessCurrency';
import type { RevenueProduct } from '../../../core/api/generated/models/RevenueProduct';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiConfirmDialogService,
  UiDetailPageSkeleton,
  UiHint,
  UiInput,
  UiLabel,
  UiPageHeader,
  UiPanelCard,
  UiPanelText,
  UiPanelTitle,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiTextarea,
} from '../../../shared/ui';
import { RevenueApiService } from './revenue-api.service';
import { ProductGalleryComponent } from './product-gallery.component';
import { productTypeLabel } from './product-types.catalog';
import {
  PRODUCT_BILLING_INTERVALS,
  PRODUCT_CUSTOM_UNIT_OPTIONS,
  type ProductBillingInterval,
  type ProductCustomIntervalUnit,
} from './product-billing.utils';

const CURRENCY_OPTIONS = Object.values(businessCurrencyEnum);

interface ProductWithFlag extends RevenueProduct {
  in_use_by_active_sales?: boolean;
}

interface CommercialDraft {
  description: string;
  amount: string;
  currency: string;
  billing_interval: ProductBillingInterval | '';
  custom_interval_value: number;
  custom_interval_unit: ProductCustomIntervalUnit;
}

function draftFromProduct(p: RevenueProduct | null): CommercialDraft {
  return {
    description: p?.description ?? '',
    amount: p?.amount ?? '',
    currency: p?.currency ?? '',
    billing_interval: (p?.billing_interval as ProductBillingInterval | undefined) ?? '',
    custom_interval_value: p?.custom_interval_value ?? 1,
    custom_interval_unit: (p?.custom_interval_unit as ProductCustomIntervalUnit | undefined) ?? 'month',
  };
}

@Component({
  selector: 'app-produto-detalhe-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NgIcon,
    NgpSelectPortal,
    ProductGalleryComponent,
    UiBadge,
    UiButton,
    UiDetailPageSkeleton,
    UiHint,
    UiInput,
    UiLabel,
    UiPageHeader,
    UiPanelCard,
    UiPanelText,
    UiPanelTitle,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiTextarea,
  ],
  providers: [
    provideIcons({
      heroArchiveBoxXMark,
      heroBanknotes,
      heroExclamationTriangle,
      heroLightBulb,
      heroPhoto,
      heroSparkles,
    }),
  ],
  templateUrl: './produto-detalhe.page.html',
  styleUrl: './produto-detalhe.page.css',
})
export class ProdutoDetalhePage {
  private readonly api = inject(RevenueApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly confirmDialog = inject(UiConfirmDialogService);

  readonly productTypeLabel = productTypeLabel;
  readonly intervalOptions = PRODUCT_BILLING_INTERVALS;
  readonly customUnitOptions = PRODUCT_CUSTOM_UNIT_OPTIONS;
  readonly currencyOptions = CURRENCY_OPTIONS;

  readonly productId = signal<string>('');
  readonly product = signal<ProductWithFlag | null>(null);
  readonly loading = signal(true);
  readonly submitting = signal(false);

  // Initial snapshot (what's saved). Used to compute `dirty`.
  readonly initialDraft = signal<CommercialDraft>(draftFromProduct(null));
  readonly draft = signal<CommercialDraft>(draftFromProduct(null));

  readonly dirty = computed(() => {
    const a = this.initialDraft();
    const b = this.draft();
    return (
      a.description !== b.description ||
      a.amount !== b.amount ||
      a.currency !== b.currency ||
      a.billing_interval !== b.billing_interval ||
      (b.billing_interval === 'custom'
        ? a.custom_interval_value !== b.custom_interval_value || a.custom_interval_unit !== b.custom_interval_unit
        : false)
    );
  });

  readonly canEdit = computed(() => {
    const p = this.product();
    return !!p && p.status === 'active' && !p.in_use_by_active_sales;
  });

  readonly saveDisabledReason = computed(() => {
    const p = this.product();
    if (!p) return '';
    if (p.status === 'archived') return 'Produto arquivado não pode ser editado.';
    if (p.in_use_by_active_sales) return 'Não é possível editar produto com vendas ativas. Arquive para descontinuar.';
    if (!this.dirty()) return 'Sem alterações para salvar.';
    return '';
  });

  readonly suggestions = computed<Array<{ tone: 'info' | 'warning'; text: string }>>(() => {
    const out: Array<{ tone: 'info' | 'warning'; text: string }> = [];
    const p = this.product();
    if (!p) return out;
    if (!p.description) {
      out.push({ tone: 'info', text: 'Adicione uma descrição para o catálogo público.' });
    }
    if (!p.amount) {
      out.push({ tone: 'warning', text: 'Defina um valor para que o produto possa ser vendido.' });
    }
    if (p.amount && !p.billing_interval) {
      out.push({ tone: 'info', text: 'Escolha um intervalo de cobrança.' });
    }
    return out;
  });

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('id') ?? '';
      this.productId.set(id);
      if (id) this.loadProduct(id);
    });
  }

  loadProduct(productId: string) {
    this.loading.set(true);
    this.api
      .getProduct(productId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (product) => {
          const p = product as ProductWithFlag;
          this.product.set(p);
          const d = draftFromProduct(p);
          this.initialDraft.set(d);
          this.draft.set(d);
          this.loading.set(false);
        },
        error: (err) => {
          this.toastr.error('Produto não encontrado.', err?.error?.message);
          this.loading.set(false);
          this.router.navigate(['/produtos']);
        },
      });
  }

  updateDescription(value: string) {
    this.draft.update((d) => ({ ...d, description: value }));
  }
  updateAmount(value: string) {
    this.draft.update((d) => ({ ...d, amount: value }));
  }
  setCurrency(value: unknown) {
    this.draft.update((d) => ({ ...d, currency: (value as string) ?? '' }));
  }
  setBillingInterval(value: unknown) {
    this.draft.update((d) => ({ ...d, billing_interval: (value as ProductBillingInterval | '') ?? '' }));
  }
  setCustomUnit(value: unknown) {
    this.draft.update((d) => ({ ...d, custom_interval_unit: (value as ProductCustomIntervalUnit) ?? 'month' }));
  }
  updateCustomValue(value: number) {
    this.draft.update((d) => ({ ...d, custom_interval_value: Number(value) || 1 }));
  }

  clearAmount() {
    this.draft.update((d) => ({ ...d, amount: '', currency: '' }));
  }

  validate(): string | null {
    const d = this.draft();
    const amountSet = d.amount.trim() !== '';
    const currencySet = d.currency.trim() !== '';
    if (amountSet !== currencySet) {
      return amountSet
        ? 'Selecione uma moeda quando informar o valor.'
        : 'Informe um valor quando selecionar uma moeda.';
    }
    if (amountSet && !/^-?\d+(\.\d{1,4})?$/.test(d.amount.trim())) {
      return 'Valor inválido. Use formato 123.45 com até 4 casas decimais.';
    }
    if (d.billing_interval === 'custom') {
      if (!d.custom_interval_value || d.custom_interval_value < 1) {
        return 'Intervalo personalizado precisa de número ≥ 1.';
      }
    }
    return null;
  }

  submit() {
    if (this.submitting() || !this.dirty()) return;
    const error = this.validate();
    if (error) {
      this.toastr.error(error);
      return;
    }
    const productId = this.productId();
    const d = this.draft();
    const isCustom = d.billing_interval === 'custom';
    const body = {
      description: d.description.trim() || null,
      amount: d.amount.trim() || null,
      currency: d.currency ? d.currency.toUpperCase() : null,
      billing_interval: d.billing_interval || null,
      custom_interval_value: isCustom ? d.custom_interval_value : null,
      custom_interval_unit: isCustom ? d.custom_interval_unit : null,
    };
    this.submitting.set(true);
    this.api
      .updateProduct(productId, body as never)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          const merged = { ...updated, in_use_by_active_sales: this.product()?.in_use_by_active_sales } as ProductWithFlag;
          this.product.set(merged);
          const next = draftFromProduct(merged);
          this.initialDraft.set(next);
          this.draft.set(next);
          this.toastr.success('Produto atualizado.');
          this.submitting.set(false);
        },
        error: (err) => {
          this.toastr.error('Não foi possível atualizar.', err?.error?.message);
          this.submitting.set(false);
        },
      });
  }

  discardChanges() {
    if (this.submitting()) return;
    this.draft.set(this.initialDraft());
  }

  async archiveProduct() {
    const p = this.product();
    if (!p) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Arquivar produto?',
      description: `"${p.name}" deixa de aparecer no catálogo. Vendas existentes não são afetadas.`,
      confirmLabel: 'Arquivar',
      tone: 'danger',
    });
    if (!ok) return;
    this.api
      .archiveProduct(p.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastr.success(`"${p.name}" arquivado.`);
          this.router.navigate(['/produtos']);
        },
        error: (err) =>
          this.toastr.error('Não foi possível arquivar.', err?.error?.message),
      });
  }
}
